"use client"

import { useSearchParams } from "next/navigation"
import { useEffect, useState, useTransition } from "react"
import { ListProductData } from "src/data/product-list/types"
import { searchProducts } from "src/data/products/searching"
import { useEventCallback } from "src/hooks/useEventCallback"
import { trackSearch } from "src/tracking/events"

function updateUrl(value: string) {
  const searchParams = new URLSearchParams(window.location.search)

  if (value) {
    searchParams.set("q", value)
  } else {
    searchParams.delete("q")
  }

  window.history.replaceState({}, "", `${window.location.pathname}?${searchParams}`)
}

let urlTimeout: any

const scheduleUpdateUrl = (value: string) => {
  clearTimeout(urlTimeout)
  urlTimeout = window.setTimeout(() => updateUrl(value), 100)
}

let trackingTimeout: any

export function useProductSearch(listId: string, products: ListProductData[]) {
  const searchParams = useSearchParams()
  const [value, setValue] = useState(searchParams.get("q") || "")
  const [delayedValue, setDelayedValue] = useState(value)
  const [transitioning, startTransition] = useTransition()
  const [filteredHits, setFilteredHits] = useState<ListProductData[]>([])

  const scheduleUpdateResults = useEventCallback((products: ListProductData[], value: string) => {
    startTransition(() => {
      const filteredHits = searchProducts(products, value)
      setFilteredHits(filteredHits)
    })
  })

  const scheduleTrackSearch = useEventCallback((listId: string, value: string) => {
    clearTimeout(trackingTimeout)
    trackingTimeout = window.setTimeout(() => {
      trackSearch({ listId, input: value })
      setDelayedValue(value)
    }, 1000)
  })

  const setQueryAndUpdateUrl = useEventCallback((products: ListProductData[], value: string) => {
    scheduleUpdateUrl(value)
    setValue(value)
    scheduleUpdateResults(products, value)
    scheduleTrackSearch(listId, value)
  })

  useEffect(() => {
    const value = new URLSearchParams(window.location.search).get("q") || ""

    setValue(value)
    scheduleUpdateResults(products, value)
    scheduleTrackSearch(listId, value)
  }, [listId, products, scheduleUpdateResults, scheduleTrackSearch])

  return {
    query: value,
    delayedValue,
    setQuery: setQueryAndUpdateUrl,
    hits: filteredHits,
  }
}
